/**
 * Vessel Configuration Schema
 * Single source of truth for all vessel fields
 * Created: October 15, 2025
 * 
 * CRITICAL: Any field added here MUST be handled in vesselConfigMapper.js
 * or it will not persist when saving vessel settings!
 */

// ============================================================================
// FIELD GROUPS FOR VALIDATION
// ============================================================================

/**
 * Fields that are required for a valid vessel configuration
 */
export const REQUIRED_FIELDS = [
  'id',
  'name',
  'type',
  'category'
];

/**
 * Fields that should trigger maintenance recalculation when changed
 */
export const MAINTENANCE_TRIGGER_FIELDS = [
  'category',
  'type',
  'grossTonnage',
  'lengthOverall',
  'engineType',
  'enginePosition',
  'propulsionConfiguration',
  'driveType',
  'hasGalley',
  'galleyHeatSource',
  'hasCooking',
  'hasOpenFlame',
  'hasHeating',
  'hasGasAppliance',
  'hasLiquidFuelAppliance',
  'hasAccommodation',
  'hasWheelhouse',
  'hasMachinerySpace',
  'hasLithiumBatteries',
  'hasFireRiskArea',
  'optionalEquipment'
];

/**
 * Fields that are computed/derived and shouldn't be directly set
 */
export const COMPUTED_FIELDS = [
  'hasRadar',
  'hasNavtex',
  'hasMF',
  'hasAIS',
  'hasLiferaft'
];

/**
 * Legacy field mappings for backward compatibility
 */
export const LEGACY_FIELD_MAPPINGS = {
  'vesselName': 'name',
  'vesselType': 'type',
  'homePort': 'port',
  'areaCategory': 'category'
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Get all fields defined in the Vessel interface
 * Used for schema-driven mapper validation
 */
export function getAllVesselFields() {
  return [
    'id', 'name', 'type', 'category',
    'imo', 'flag', 'port', 'homePort', 'grossTonnage', 'lengthOverall', 
    'draft', 'enginePower', 'age',
    'engineType', 'enginePosition', 'numberOfEngines', 'propulsionType', 
    'propulsionConfiguration', 'driveType',
    'steeringType', 'steeringPosition', 'engineStartMethod', 
    'steeringControlType', 'hasOilLubricatedShaft', 'hasWorkboatPilotEndorsement',
    'maxPersons', 'passengerCapacity', 'crewCount', 'crewCapacity',
    'operatingStatus', 'operatingHours', 'internationalVoyages', 'maxVoyageDistance',
    'towing_capable', 'engineer_onboard',
    'hasAccommodation', 'hasGalley', 'galleyHeatSource', 'hasCooking', 'hasOpenFlame', 'hasHeating',
    'hasGasAppliance', 'hasLiquidFuelAppliance',
    'openFlameProtection', 'engineBoxProtection',
    'fixedFFE', 'fixedSystems',
    'hasWheelhouse',
    'hasMachinerySpace', 'hasEnclosedSpaces', 'enclosed_accommodation_present',
    'cabin_accommodation', 'hasGasAppliances', 'hasLPGSystem',
    'hasLithiumBatteries', 'hasConcentratedElectrical', 'hasExhaustLeakRisk', 
    'hasGasLeakRisk', 'hasFireRiskArea', 'gasAppliances',
    'hasEmergencySteering', 'power_critical_systems', 'bilge_auto', 'hasShorepower',
    'isPracticable', 'deemedNecessary', 'companyRequiresBuckets',
    'oilCapacityLitres', 'hasProhibitedDischargeArea', 'hasIMOSewageSystem', 'isUnmanned',
    'equipment', 'operations', 'optionalEquipment', 'specialEquipment', 'companyRequiredDrills',
    'navigationEquipment', 'communicationEquipment', 'electricalSystems', 'engineeringSystems', 'operationalEquipment', // FT-10 FIX: Wizard Step 6 equipment selections
    'gmdssSeaArea', 'seaArea', 'seaAreaA1', 'seaAreaA2', 'seaAreaA3', 'seaAreaA4', // Sea area fields
    'mobEquipment', 'lsaEquipment', // CRITICAL: wizard persistence fields
    'hasRadar', 'hasNavtex', 'hasMF', 'hasAIS', 'hasLiferaft',
    'hasSART', 'hasGrabBag', 'hasStabilityBooklet', // Safety equipment flags for familiarisation
    'equipmentCheckSettings',
    'liferafts_fitted', 'liferaft_capacity', 'hru_fitted', 'hruType',
    'lifeboat_fitted', 'rescue_boat_fitted',
    'waterCategory', 'carriesVulnerablePassengers', 'operatesAtNight', 
    'allPersonsWearLifejackets', 'hasDecking', 'hasHeating', 
    'inlandRegulatoryPath', 'lifejacketType',
    'certificates',
    'timezone', // FT-269: vessel timezone (IANA name) for voyage event display + snapshotting
    'createdAt', 'updatedAt',
    'vesselType', 'vesselName', 'areaCategory'
  ];
}

/**
 * Validate that a vessel has all required fields
 */
export function validateVessel(vessel) {
  const errors = [];
  
  for (const field of REQUIRED_FIELDS) {
    if (!vessel[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  }
  
  return errors;
}

/**
 * Check if a field name is a valid vessel field
 */
export function isValidVesselField(fieldName) {
  return getAllVesselFields().includes(fieldName);
}

/**
 * Get the canonical field name (handles legacy mappings)
 */
export function getCanonicalFieldName(fieldName) {
  return LEGACY_FIELD_MAPPINGS[fieldName] || fieldName;
}